// src/Pages/Landing/TrubaduuriPirkanmaaLanding.jsx
import React, { useMemo } from "react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import PriceInfoBox from "../../components/PriceInfoBox/PriceInfoBox.jsx";
import { trackLandingCta } from "../../utils/analytics";

/**
 * Trubaduuri Pirkanmaalla -landing React Shelliin.
 * Metat ja JSON-LD tulevat WordPressistä.
 * Hintaboksi käyttää Pirkanmaan keskihintoja (regionSlug="pirkanmaa").
 */

const PIRKANMAA_SLUG = "pirkanmaa";

export default function TrubaduuriPirkanmaaLanding() {
  // Paikallisen päivän ISO (YYYY-MM-DD)
  const today = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  return (
    <section className="relative max-w-6xl mx-auto px-4 pt-24 pb-24">
      {/* Hero / ingressi */}
      <header className="rounded-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-md p-8 md:p-12 shadow-lg">
        <h1 className="text-4xl md:text-6xl font-bold leading-tight text-black dark:text-white">
          Trubaduuri Pirkanmaalla – vapaat esiintyjät
        </h1>

        <p className="mt-4 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Buukkerista löydät Pirkanmaan vapaat trubaduurit reaaliaikaisesti.{" "}
          <strong>
            Valitse päivämäärä, katso vapaat esiintyjät ja pyydä tarjous parilla
            klikkauksella.
          </strong>
        </p>

        <p className="mt-3 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Tarjottu hinta on <strong>kokonaishinta (sis. matkakulut)</strong>.
          Lisäksi esiintyjältä voi tilata lisäpalveluita tarpeen mukaan –
          esimerkiksi taustamusiikkia illan alkuun, häävalssin, lisävaloja tai
          DJ:n jatkamaan iltaa.
        </p>

        {/* CTA-napit */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <a
            href={`/?type=trubaduuri&maakunta=pirkanmaa&date=${today}&clear=1#tulokset`}
            aria-label="Näytä tämän päivän vapaat trubaduurit (Pirkanmaa)"
            onClick={() =>
              trackLandingCta({
                actType: "trubaduuri",
                region: "pirkanmaa",
                placement: "hero_primary_today",
              })
            }
          >
            <NeonButton variant="primary" size="sm" className="font-semibold">
              Näytä tämän päivän vapaat (Pirkanmaa)
            </NeonButton>
          </a>

          <a
            href={`/?type=trubaduuri&book=1&maakunta=pirkanmaa&clear=1#tulokset`}
            aria-label="Avaa pikahaku trubaduureille"
            onClick={() =>
              trackLandingCta({
                actType: "trubaduuri",
                region: "pirkanmaa",
                placement: "hero_quick_search",
              })
            }
          >
            <NeonButton variant="secondary" size="sm" className="font-semibold">
              Avaa pikahaku
            </NeonButton>
          </a>
        </div>
      </header>

      {/* Hintainfoboksi: trubaduurien keskihinnat Pirkanmaalla */}
      <div className="mt-8">
        <PriceInfoBox
          title="Trubaduurien keskihinnat Pirkanmaalla – suosituimmat setit"
          types={["trubaduuri"]}
          autoTopSets
          topN={3}
          regionSlug={PIRKANMAA_SLUG}      // ← haetaan Pirkanmaa-keskiarvo
          refreshMs={15 * 60 * 1000}       // päivitys 15 min välein
          compact={true}
          nationalLabel="Buukkerin keskihinta"
        />
        <p className="mt-2 text-black/80 dark:text-white/80 text-sm opacity-70">
          Hinta on suuntaa-antava keskiarvo Pirkanmaalta.{" "}
          <strong>Lopullinen hinta täsmentyy tarjouspyynnössä</strong> ja se
          sisältää myös matkakulut. Hinnat perustuvat Buukkerin trubaduurien
          hinnastoihin, joissa{" "}
          <strong>
            Pirkanmaa on merkitty keikkailualueeksi tai esiintyjän kotipaikaksi
          </strong>{" "}
          – ei pelkästään yksittäiseen kaupungin nimeen.
        </p>
      </div>

      {/* Ristiinlinkitys */}
      <div className="mt-8 rounded-2xl p-6 md:p-8 bg-white/70 dark:bg-zinc-900/60 ring-1 ring-black/10 dark:ring-white/10 shadow-lg">
        <p className="text-black/80 dark:text-white/80 text-base md:text-lg">
          Tarvitsetko bilebändiä tai DJ:tä Pirkanmaalla? Siirry{" "}
          <a className="underline" href="/bilebandi-pirkanmaa/">
            Bilebändi Pirkanmaa
          </a>{" "}
          tai{" "}
          <a className="underline" href="/dj-pirkanmaa/">
            DJ Pirkanmaa
          </a>{" "}
          -sivulle. Lue myös:{" "}
          <a
            className="underline"
            href="https://buukkeri.fi/blogi/nain-loydat-taydellisen-esiintyjan-juhliisi"
          >
            Näin löydät täydellisen esiintyjän juhliisi
          </a>
          .
        </p>
      </div>
    </section>
  );
}
